import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setStatus, unselectChange, setDuration, setCycle } from './editorSlice'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { NUMERAL_TYPES, NUM_STEPS_PER_BAR, MAX_BARS } from './editorConstants'
import { transformStepToMeter, getChangeLength, getPitchJSX, getNumeral, getNumeralJSX, getTypeJSX,  getProgression, getProgressionJSX, useKeyPressing, bufferToWav, calculateDurationInSeconds } from './editorUtils'
import { Drawer, CircularProgress, IconButton, Slide, SvgIcon, Divider, Button } from '@mui/material'
import StyledTooltip from '../../components/StyledTooltip'
import { withStyles } from '@mui/styles'
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import LoopMap from './LoopMap'
import { ReactComponent as FxSelectedIcon } from './icons/fx-selected.svg'
import { ReactComponent as FxUnselectedIcon } from './icons/fx-unselected.svg'
import {ReactComponent as FillSelectedIcon} from './icons/fill-selected.svg'
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import KeyboardArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardArrowLeftOutlined';
import {ReactComponent as GrooveLoopIcon} from './icons/groove-loop.svg'
import {ReactComponent as GrooveOneShotIcon} from './icons/groove-one-shot.svg'
import {ReactComponent as LickLoopIcon} from './icons/lick-loop.svg'
import {ReactComponent as LickOneShotIcon} from './icons/lick-one-shot.svg'
import RepeatIcon from '@mui/icons-material/Repeat';
import RepeatOnIcon from '@mui/icons-material/RepeatOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LaptopOutlinedIcon from '@mui/icons-material/LaptopOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import ReplyIcon from '@mui/icons-material/Reply';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import useWindowSize from '../../hooks/window-size';
import './Viewer.css'
import Video from '../help/video'

const Wrap = (props) => {
  const { open, url, name, alreadyAdd, onClose, onPlay, onAdd, onDownload, onShare, backgroundImage, editOwnLoop } = props;
  const dispatch = useDispatch();
  const status = useSelector(state => state.editor.status);
  const fx = useSelector(state => state.editor.fx);
  const bars = useSelector(state => state.editor.bars);
  const bpm = useSelector(state => state.editor.bpm);
  const key = useSelector(state => state.editor.key);
  const mode = useSelector(state => state.editor.mode);
  const clips = useSelector(state => state.editor.clips);
  const cycle = useSelector(state => state.editor.cycle);
  const [wavesurfer, setWavesurfer] = useState(null);
  const spaceKeyPressing = useKeyPressing('Space', 32);
  const progression = getProgression(clips, bars);
  const selectedClipIndex = useSelector(state => state.editor.selectedClipIndex);
  const selectedChangeIndex = useSelector(state => state.editor.selectedChangeIndex);
  const clip = clips[selectedClipIndex];
  const change = selectedClipIndex !== -1 && clip.changes[selectedChangeIndex];
  const [isDownloading, setDownloading] = useState(false);
  const [added, setAdded] = useState(false);
  const [showFx, setShowFx] = useState(false);
  const [showFill, setShowFill] = useState(false);
  const { width } = useWindowSize();
  const [videoModel, setVideoModel] = useState(false);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent || '') || /iPad|iPhone|iPod/i.test(navigator.userAgent || '');

  const StyledDivider = withStyles({
    root: {
        backgroundColor: '#FFFFFF',
        opacity: 0.3,
        marginBottom: 10
    }
  })(Divider);
  
  const StyledButton = withStyles({
    root: {
        minWidth: 16,
        padding: 0,
        marginLeft: width >= 1042 ? 24: 8,
        color: 'white',
        textTransform: 'none'
    },
    text: {
      color: 'white'
    }
  })(Button);

  useEffect(() => {
    if (onClose && !open)
    {
      dispatch(unselectChange());
      if (wavesurfer !== null && status !== 'editing')
      {
        wavesurfer.stop();
        dispatch(setStatus('editing'));
      }
    }
  }, [open]);

  useEffect(() => {
    if (spaceKeyPressing && (status === 'editing' || status === 'playing'))
      handlePlayStopButtonClick();
  }, [spaceKeyPressing]);

  function Attribute(props)
  {
    const {name, value} = props;
    return (
      <div className='viewer-attribute'>
        <div className='viewer-attribute-name'>{name}</div>
        <div className='viewer-attribute-value'>{value}</div>
      </div>
    )
  }

  function handlePlayStopButtonClick()
  {
    if (wavesurfer !== null && status === 'playing')
    {
        wavesurfer.stop();
        dispatch(setStatus('editing'));
    }
    else if(wavesurfer !== null)
    {
        if (wavesurfer.isPlaying())
            wavesurfer.stop();
        dispatch(setStatus('requesting'));
        if (onPlay)
          onPlay();
        wavesurfer.unAll();
        wavesurfer.load(url); 
        wavesurfer.on("ready", () => {
            let duration = wavesurfer.getDuration();
            let diff = duration - calculateDurationInSeconds(bpm, bars);
            if (diff < 0)
            {
              diff = 0;
              duration = calculateDurationInSeconds(bpm, bars);
            }
            else if (isSafari)
              diff += 0.1;
            dispatch(setDuration(duration));
            dispatch(setStatus('playing'));
            // wavesurfer.clearRegions();
            // const region = wavesurfer.addRegion({
            //   start: diff / 2,
            //   end: duration - diff / 2,
            //   loop: cycle,
            //   drag: false,
            //   resize: false,
            //   color: 'rgba(0, 0, 0, 0)'
            // });
            // const promise = cycle ? region.playLoop() : wavesurfer.play();
            const promise = wavesurfer.play();
            if (promise !== undefined) {
              promise.catch(error => {
                dispatch(setStatus('editing'));
              })
            }
        })
        wavesurfer.on("finish", () => {
            dispatch(setStatus('editing'));
        })
    }
  }
  const iconSize = width < 768 ? 25 : 25;

  function addLoop()
  {
    if (alreadyAdd || added)
      return;
    setAdded(true);
    onAdd().then((r) => !r && setAdded(false));
  }

  return (
    <DndProvider backend={HTML5Backend}>
        <div className='viewer'>
          <div className='viewer-bg-img' style={{backgroundImage: `url("${backgroundImage}")`}}></div>
          <div className='viewer-bg-fc'></div>
          <div className='viewer-upper-right'>
            {/* <StyledButton variant="text" onClick={() => dispatch(setCycle(!cycle))} startIcon={cycle ? <RepeatOnIcon style={{width: iconSize - 3, height: iconSize - 3, marginRight: 4}}/> : <RepeatIcon style={{width: iconSize, height: iconSize}}/>}>{width >= 1042 && 'Cycle'}</StyledButton>  */}
            {!editOwnLoop && <StyledButton variant="text" onClick={addLoop}
            startIcon={alreadyAdd || added ? <CheckCircleIcon style={{width: iconSize, height: iconSize}}/> : <AddCircleOutlineOutlinedIcon style={{width: iconSize, height: iconSize}}/> }>
              {width >= 1042 && (alreadyAdd || added ? 'Added' : 'Add')}
            </StyledButton>}
            {width >= 768 && <StyledButton variant="text" onClick={() => {setDownloading(true); onDownload().then((r) => {setDownloading(false); r && addLoop()})}} 
            startIcon={isDownloading ? <CircularProgress style={{width: iconSize, height: iconSize, color: 'white', opacity: 0.5}}/> : <SaveAltIcon style={{width: iconSize, height: iconSize}}/> }>{width >= 1042 && 'Download and add'}</StyledButton>}
            <StyledButton variant="text" onClick={onShare} startIcon={<ReplyIcon style={{width: iconSize, height: iconSize, transform: 'scaleX(-1)'}}/>}>{width >= 1042 && 'Share'}</StyledButton> 
          </div>
          <div className='viewer-header'>
            {status === 'requesting' ? <CircularProgress style={{color: 'white', opacity: 0.5, marginLeft: width < 768 ? 27 : 26, width: width < 768 ? 48 : 32, height: width < 768 ? 48 : 32, marginRight: 27, marginTop: width < 768 ? 13 : 0, marginBottom: width < 768 ? 15 : 0}} /> : 
            <IconButton color='inherit' style={{ fontSize: width < 768 ? '60px' : '44px', marginLeft: width < 768 ? 15 : 10, marginRight: 15, position: 'relative', color: 'white'}} onClick={() => handlePlayStopButtonClick()}>
                <CircleOutlinedIcon style={{ opacity: 0.5}} fontSize='inherit'/>
                {status === 'playing' ? <StopIcon style={{position: 'absolute', fontSize: 28}}/>
                : <PlayArrowIcon style={{position: 'absolute', fontSize: 28}}/>}
            </IconButton>
            }
            <div className='viewer-name'>{name}</div>
          </div>
          {/* <LoopMap setWavesurfer={setWavesurfer} offsetX={width < 768 ? 15 : 8} stepWidth={width < (onClose ? 1042 : 978) ? (width - (onClose? 82 : 28)) / (NUM_STEPS_PER_BAR * MAX_BARS) : 15} isViewOnly={true}/> */}
          <LoopMap setWavesurfer={setWavesurfer} offsetX={width < 768 ? 15 : 8} stepWidth={width < 978 ? (width - 28) / (NUM_STEPS_PER_BAR * MAX_BARS) : 15} isViewOnly={true}/>
          <div className='viewer-attributes'>
            {selectedClipIndex === -1 && selectedChangeIndex === -1 &&
            <Slide direction={width < 768 ? 'right' : "down"} in={selectedClipIndex === -1 && selectedChangeIndex === -1}  mountOnEnter unmountOnExit>
              <div>
                <div className='viewer-notice'>
                  <div style={{display: 'flex', alignItems: 'center'}} onClick={() => setVideoModel(true)}><LaptopOutlinedIcon style={{marginLeft: -2, marginRight: 15}}/>Edit on desktop</div>
                  <StyledDivider style={{marginLeft: -16, marginTop: 4}}/>
                  <Attribute name='FX' value={fx !== null ? fx : 'None'} />
                </div>
                <div className='viewer-loop-attributes'>
                  <StyledTooltip open={fx !== null && showFx} placement='top' arrow title={fx}>
                    <div className='viewer-icon' onMouseEnter={() => setShowFx(true)} onMouseLeave={() => setShowFx(false)} >
                      <IconButton disabled disableRipple style={{marginRight: 7}}>
                          {fx !== null ? <FxSelectedIcon /> : <FxUnselectedIcon />}
                      </IconButton>
                    </div>
                  </StyledTooltip>
                  <Attribute name='Bars' value={bars} />
                  <Attribute name='Key' value={<React.Fragment>{getPitchJSX(key)}{mode === 'major' ? '' : 'm'}</React.Fragment>} />
                  <Attribute name='Bpm' value={bpm} />
                  <div className='viewer-progression'>
                    <div className='viewer-attribute-name'>Progression</div>
                    <div className='viewer-attribute-value' style={{display: 'flex'}}>{getProgressionJSX(progression, key)}</div>
                  </div>
                </div>
              </div>
            </Slide>
            }
            {selectedClipIndex !== -1 && selectedChangeIndex !== -1 &&
            <Slide direction={width < 768 ? 'left' : "up"} in={selectedClipIndex !== -1 && selectedChangeIndex !== -1}  mountOnEnter unmountOnExit>
              <div className='viewer-clip-attributes'>
                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                  <div className='viewer-clip-name'>
                    <SvgIcon style={{marginTop: '8px', marginRight: '10px'}} 
                                component={clip.type === 'groove' ? (clip.beats === 0 ? GrooveOneShotIcon : GrooveLoopIcon) : (clip.beats === 0 ? LickOneShotIcon : LickLoopIcon)}/>
                    <div style={{fontSize: 14,  whiteSpace: 'nowrap', color: 'white', transform:'translateY(2px)'}}>{clip.name}</div>
                  </div>
                  {width < 768 && <IconButton disableRipple style={{margin:0, padding: 0, marginRight: 5}}onClick={() =>  dispatch(unselectChange())}><KeyboardArrowLeftOutlinedIcon style={{fontSize: 32, color: 'white'}} /></IconButton>}
                </div>
                <StyledDivider/>
                <div className='viewer-change-attributes'>
                  <Attribute name='Start' value={transformStepToMeter(clip.loopStart + change.start, 1)} />
                  <Attribute name='Length' value={transformStepToMeter(getChangeLength(clip, selectedChangeIndex))} />
                  {clip.type === 'groove' &&
                    <Attribute name='Chord' value={<React.Fragment>{getNumeralJSX(getNumeral(key, change.root))}{' '}{getPitchJSX(change.root)}{getTypeJSX(clip.numeral ? NUMERAL_TYPES[change.numeral] : change.type)}</React.Fragment>} />
                  }
                  <Attribute name='Register' value={change.position} />
                  {change.fill !== undefined && change.fill !== null && 
                    <StyledTooltip open={change.fill !== null && showFill} placement='top' arrow title={'Fill ' + change.fill}>
                      <div className='viewer-icon' onMouseEnter={() => setShowFill(true)} onMouseLeave={() => setShowFill(false)}><IconButton disableRipple disabled style={{marginLeft: -14}}><FillSelectedIcon /></IconButton></div>
                    </StyledTooltip>}
                  {width >= 768 && 
                    <IconButton disableRipple onClick={() =>  dispatch(unselectChange())}><KeyboardArrowUpOutlinedIcon style={{fontSize: 32, color: 'white'}} /></IconButton>
                  }
                </div>
              </div>
            </Slide>
            }
          </div>
        </div>
        <Video open={videoModel} onClose={() => setVideoModel(false)} />
      </DndProvider>
  );
}


export default function Viewer(props) {
  const { open, onClose } = props;
  const json = useSelector(state => state.editor.json);

  return ( onClose ? 
    <Drawer open={json !== null && open} anchor='bottom' onClose={onClose}>
      <Wrap {...props} />
    </Drawer> : <Wrap {...props} />
  );
};